<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePortalAuthStore } from '@/stores/portalAuth'
import { usePortalNotificationsStore } from '@/stores/portalNotifications'
import { usePortalOrganizationsStore } from '@/stores/portalOrganizations'
import { useUiStore } from '@/stores/ui'
import AppBreadcrumb from '@/components/shared/AppBreadcrumb.vue'
import NotificationBell from '@/components/shared/NotificationBell.vue'
import PortalNotificationDropdown from '@/components/shared/PortalNotificationDropdown.vue'
import PortalOrganizationSwitcher from '@/components/portal/PortalOrganizationSwitcher.vue'
import isotypeSrc from '@/assets/isotype.svg'
import logoIndigoSrc from '@/assets/logo-indigo.svg'

const router = useRouter()
const route = useRoute()
const portalAuth = usePortalAuthStore()
const notifications = usePortalNotificationsStore()
const organizations = usePortalOrganizationsStore()
const ui = useUiStore()

const showDropdown = ref(false)

function toggleDropdown() {
  showDropdown.value = !showDropdown.value
}

function closeDropdown() {
  showDropdown.value = false
}

watch(
  () => portalAuth.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      notifications.startPolling()
      void organizations.fetchAll()
    } else {
      notifications.stopPolling()
      organizations.reset()
      closeDropdown()
    }
  },
)

onMounted(() => {
  if (!portalAuth.isAuthenticated) return
  notifications.startPolling()
  void organizations.fetchAll()
})

onUnmounted(() => {
  notifications.stopPolling()
})

function handleOrganizationChange() {
  window.location.assign('/portal')
}

async function handleLogout() {
  notifications.stopPolling()
  organizations.reset()
  await portalAuth.logout()
  router.push({ name: 'portal-login' })
}
</script>

<template>
  <div class="flex h-screen flex-col bg-gray-50">
    <header class="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 bg-indigo-700 px-6">
      <div class="flex items-center gap-3">
        <img
          :src="isotypeSrc"
          alt="Plutto"
          class="h-7 w-7"
        >
        <span class="text-lg font-semibold tracking-tight text-white">Portal de Proveedores</span>
      </div>

      <div class="flex items-center gap-4">
        <div
          v-if="portalAuth.supplierUser"
          class="flex items-center gap-2"
        >
          <div class="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-sm font-medium text-white">
            {{ portalAuth.supplierUser.name.charAt(0).toUpperCase() }}
          </div>
          <span class="hidden text-sm font-medium text-white/90 sm:block">
            {{ portalAuth.supplierUser.name }}
          </span>

          <PortalOrganizationSwitcher @change="handleOrganizationChange" />

          <div class="relative [&>button]:text-white/70 [&>button:hover]:bg-white/10 [&>button:hover]:text-white">
            <NotificationBell
              :count="notifications.unreadCount"
              :expanded="showDropdown"
              @click="toggleDropdown"
            />
            <PortalNotificationDropdown
              :visible="showDropdown"
              @close="closeDropdown"
            />
          </div>

          <router-link
            :to="{ name: 'portal-settings' }"
            class="rounded-lg p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
            :class="{ 'bg-white/10 text-white': route.name === 'portal-settings' }"
            title="configuración"
            aria-label="configuración"
          >
            <span class="plutto-icon plutto-icon--outlined text-xl">settings</span>
          </router-link>
          <button
            class="rounded-lg p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            @click="handleLogout"
          >
            <span class="plutto-icon plutto-icon--outlined text-xl">logout</span>
          </button>
        </div>
      </div>
    </header>

    <div
      v-if="ui.breadcrumbs.length"
      class="border-b border-gray-200 bg-white"
    >
      <div class="mx-auto max-w-6xl px-4 py-1.5 sm:px-6 sm:py-2">
        <AppBreadcrumb />
      </div>
    </div>

    <main class="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
      <div class="mx-auto max-w-6xl">
        <slot />
      </div>
    </main>

    <footer class="border-t border-gray-200 bg-white px-6 py-3">
      <div class="flex items-center gap-2 text-gray-400">
        <span class="text-xs">Desarrollado por</span>
        <img
          :src="logoIndigoSrc"
          alt="Plutto"
          class="h-4 opacity-50"
        >
      </div>
    </footer>
  </div>
</template>
